/**
 * HTTP 전송 계층.
 *
 * tRPC 라우터가 아직 없어(AppRouter 타입 부재) 지금은 REST 로 말한다.
 * 화면은 이 파일을 직접 쓰지 않고 entity 별 쿼리 훅만 보므로,
 * 라우터가 생기면 여기만 tRPC 클라이언트로 갈아끼우면 된다.
 */
import type { z } from 'zod';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly path: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** 응답을 스키마로 검증한다 — 서버 응답도 외부 데이터이므로 믿지 않는다. */
export async function getJson<TSchema extends z.ZodType>(
  path: string,
  schema: TSchema,
  init?: RequestInit,
): Promise<z.infer<TSchema>> {
  const response = await fetch(path, {
    headers: { accept: 'application/json' },
    ...init,
  });

  if (!response.ok) {
    throw new ApiError(response.status, path, `요청에 실패했습니다 (${response.status})`);
  }

  const parsed = schema.safeParse(await response.json());
  if (!parsed.success) {
    throw new ApiError(200, path, '서버 응답 형식이 예상과 다릅니다');
  }

  return parsed.data;
}

/** 상태를 바꾸는 요청. 응답도 외부 데이터이므로 같은 방식으로 검증한다. */
export async function postJson<TSchema extends z.ZodType>(
  path: string,
  body: unknown,
  schema: TSchema,
): Promise<z.infer<TSchema>> {
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new ApiError(response.status, path, `요청에 실패했습니다 (${response.status})`);
  }

  const parsed = schema.safeParse(await response.json());
  if (!parsed.success) {
    throw new ApiError(200, path, '서버 응답 형식이 예상과 다릅니다');
  }

  return parsed.data;
}
